"use client";
import { jsxs, jsx } from "react/jsx-runtime";
import { useRef, useState, useMemo, useEffect } from "react";
import { Box } from "@mui/material";
import { Button } from "@nxt-costco-com/forge-components";
import { FontSize300, ColorGray900, SpaceXxs } from "@nxt-costco-com/forge-design-tokens";
import { ChevronDown } from "@nxt-costco-com/forge-icons/icons/ChevronDown";
import { ChevronUp } from "@nxt-costco-com/forge-icons/icons/ChevronUp";
import { Analytics } from "../../Analytics/index.js";
import { HEIGHT_PADDING_OFFSET, DOM_UPDATE_DELAY, COLLAPSED_HEIGHT, SCROLL_IGNORE_TIMEOUT, SCROLL_TOP_THRESHOLD_PERCENTAGE } from "./constants.js";
function getKeyValue(key, entry) {
  var _a;
  return ((_a = entry.key_value.find((item) => item.key === key)) == null ? void 0 : _a.value) || "";
}
function Wrapper({
  children,
  sx = {},
  buttonConfig,
  uniqueId,
  closeOnScroll = false
}) {
  const wrapperRef = useRef(null);
  const innerRef = useRef(null);
  const ignoreScrollRef = useRef(false);
  const [showMore, setShowMore] = useState(false);
  const [expanded, setIsExpanded] = useState(false);
  const [expandedHeight, setExpandedHeight] = useState(46);
  const buttonText = useMemo(() => {
    return expanded ? getKeyValue("seeless", buttonConfig) : getKeyValue("seemore", buttonConfig);
  }, [expanded, buttonConfig]);
  function hide(wrapperRef2, innerRef2) {
    if (!wrapperRef2.current || !innerRef2.current) {
      return;
    }
    let maxWidth = innerRef2.current.clientWidth;
    Array.from(innerRef2.current.children).forEach((child) => {
      maxWidth -= child.clientWidth + 6;
      if (maxWidth < 0) {
        setShowMore(true);
        child.classList.add("not-visible");
        child.setAttribute("tabindex", "-1");
      }
    });
  }
  function show() {
    if (!wrapperRef.current || !innerRef.current) {
      return;
    }
    let firstFocused = false;
    Array.from(innerRef.current.children).forEach((child) => {
      const tabIndex = child == null ? void 0 : child.getAttribute("tabindex");
      if (child && tabIndex && parseInt(tabIndex) < 0) {
        child.classList.remove("not-visible");
        child.removeAttribute("tabindex");
        if (!firstFocused) {
          child.focus({ focusVisible: true });
          firstFocused = true;
        }
      }
    });
  }
  useEffect(() => {
    if (wrapperRef.current && innerRef.current) {
      if (expanded) {
        show();
        setTimeout(() => {
          if (innerRef.current) {
            const fullHeight = innerRef.current.scrollHeight + HEIGHT_PADDING_OFFSET;
            setExpandedHeight(fullHeight);
          }
        }, DOM_UPDATE_DELAY);
      } else {
        hide(wrapperRef, innerRef);
      }
    }
  }, [wrapperRef, innerRef, expanded]);
  useEffect(() => {
    const handleLinkClick = (event) => {
      const target = event.target;
      const linkElement = target.closest("a");
      if (linkElement && expanded) {
        setIsExpanded(false);
      }
    };
    const currentInnerRef = innerRef.current;
    if (currentInnerRef) {
      currentInnerRef.addEventListener("click", handleLinkClick);
    }
    return () => {
      if (currentInnerRef) {
        currentInnerRef.removeEventListener("click", handleLinkClick);
      }
    };
  }, [expanded]);
  useEffect(() => {
    if (!closeOnScroll) return;
    const handleScroll = () => {
      if (!wrapperRef.current || !expanded || ignoreScrollRef.current)
        return;
      const rect = wrapperRef.current.getBoundingClientRect();
      const windowHeight = window.innerHeight;
      const topThreshold = windowHeight * SCROLL_TOP_THRESHOLD_PERCENTAGE;
      if (rect.top <= topThreshold) {
        setIsExpanded(false);
      }
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, [closeOnScroll, expanded]);
  return /* @__PURE__ */ jsxs(Box, { sx, children: [
    /* @__PURE__ */ jsx(
      Box,
      {
        sx: {
          height: expanded ? `${expandedHeight}px` : COLLAPSED_HEIGHT,
          overflow: "hidden",
          padding: "2px",
          transition: "height 0.2s ease-in-out"
        },
        ref: wrapperRef,
        children: /* @__PURE__ */ jsx(
          Box,
          {
            sx: {
              display: "flex",
              flexWrap: "wrap",
              gap: "6px",
              justifyContent: "left"
            },
            ref: innerRef,
            children
          }
        )
      }
    ),
    showMore && /* @__PURE__ */ jsx(
      Analytics,
      {
        analyticData: {
          component: {
            identifier: uniqueId,
            type: "nav"
          },
          title: buttonText
        },
        children: /* @__PURE__ */ jsxs(
          Button,
          {
            onClick: () => {
              ignoreScrollRef.current = true;
              setIsExpanded(!expanded);
              setTimeout(() => {
                ignoreScrollRef.current = false;
              }, SCROLL_IGNORE_TIMEOUT);
            },
            "aria-expanded": expanded,
            sx: {
              color: ColorGray900,
              fontSize: FontSize300,
              width: "100px"
            },
            variant: "text-button",
            children: [
              buttonText,
              expanded ? /* @__PURE__ */ jsx(
                ChevronUp,
                {
                  style: {
                    marginLeft: SpaceXxs,
                    width: "20px"
                  }
                }
              ) : /* @__PURE__ */ jsx(
                ChevronDown,
                {
                  style: {
                    marginLeft: SpaceXxs,
                    width: "20px"
                  }
                }
              )
            ]
          }
        )
      }
    )
  ] });
}
export {
  Wrapper
};
